import type { GridApi, IRowNode, ValueGetterParams } from 'ag-grid-enterprise'
import { AcmLevel3CreditDebitEnum } from '~/protos/gen/stcs_comp_acc_mapping.entity'
import type { StcsCompAccMappingFstmt } from '~/protos/gen/stcs_comp_acc_mapping.entity'
import { StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import type { StcsOrgFsdFlatItem } from '~/protos/gen/stcs_org_fsd_flat.entity'
import { buildHierarchy, findRowByEndsWith } from '~/services/commons.service'
import { useAcmStore } from '~/stores/acm.store'
import { useNuxtApp } from '#imports'

const DEBIT_CREDIT_REGEX = /^(debit_|credit_)/

export function convertL3UnspecifiedToDebitCredit(obj: StcsCompAccMappingFstmt | undefined): StcsCompAccMappingFstmt | undefined {
  if (!obj) {
    return
  }

  for (const l2Key in obj.acm_map_level_2) {
    const l2 = obj.acm_map_level_2[l2Key]

    for (const l3Key in l2.acm_map_level_3) {
      const l3 = l2.acm_map_level_3[l3Key]

      if (l3.level_3_credit_debit === AcmLevel3CreditDebitEnum.ACM_LEVEL_3_CD_UNSPECIFIED) {
        l3.level_3_credit_debit = AcmLevel3CreditDebitEnum.ACM_LEVEL_3_CREDIT_AND_DEBIT
      }
    }
  }

  return obj
}

export function updateBsMapListAndRemoveFromPnl(targetNodeId: string, selectedNodeId: string, pathLevelId: string) {
  const { bsMapList, pnlMapList } = useAcmStore()

  if (!bsMapList) {
    return
  }

  if (!pnlMapList) {
    return
  }

  // update the bsMapList
  bsMapList.acm_map_level_2[targetNodeId] = {
    acm_map_level_3: {
      ...bsMapList?.acm_map_level_2?.[targetNodeId]?.acm_map_level_3,
      [selectedNodeId]: {
        ...pnlMapList.acm_map_level_2[pathLevelId]?.acm_map_level_3[selectedNodeId],
        level_3_credit_debit: AcmLevel3CreditDebitEnum.ACM_LEVEL_3_CREDIT_AND_DEBIT,
      },
    },
  }

  //remove from Pnlmaplist
  if (pnlMapList) {
    delete pnlMapList.acm_map_level_2[pathLevelId].acm_map_level_3[selectedNodeId]
  }
}

export function updatePnlMapListAndRemoveFromBs(targetNodeId: string, selectedNodeId: string, pathLevelId: string) {
  const { bsMapList, pnlMapList } = useAcmStore()

  if (!bsMapList) {
    return
  }

  if (!pnlMapList) {
    return
  }

  // update the pnlMapList
  pnlMapList.acm_map_level_2[targetNodeId] = {
    acm_map_level_3: {
      ...pnlMapList?.acm_map_level_2?.[targetNodeId]?.acm_map_level_3,
      [selectedNodeId]: {
        ...bsMapList.acm_map_level_2[pathLevelId]?.acm_map_level_3[selectedNodeId],
        level_3_credit_debit: AcmLevel3CreditDebitEnum.ACM_LEVEL_3_CD_UNSPECIFIED,
      },
    },
  }

  //remove from Bsmaplist
  if (bsMapList) {
    delete bsMapList.acm_map_level_2[pathLevelId].acm_map_level_3[selectedNodeId]
  }
}

export function updateCurrentMapListAndRemoveFromOther(targetNodeId: string, selectedNodeId: string, pathLevelId: string, statement: StcsFstmtEnum) {
  const { bsMapList, pnlMapList } = useAcmStore()

  const listRefsMap: Record<number, StcsCompAccMappingFstmt | undefined> = {
    [StcsFstmtEnum.SFSE_PNL]: pnlMapList,
    [StcsFstmtEnum.SFSE_BS]: bsMapList,
    [StcsFstmtEnum.SFSE_CF]: undefined,
  }

  const listRefs = listRefsMap[statement]

  if (!bsMapList) {
    return
  }

  if (!pnlMapList) {
    return
  }

  if (!listRefs) {
    return
  }

  if (targetNodeId === pathLevelId) {
    return
  }

  listRefs.acm_map_level_2[targetNodeId] = {
    acm_map_level_3: {
      ...listRefs?.acm_map_level_2?.[targetNodeId]?.acm_map_level_3,
      [removeDebitCreditFromKey(selectedNodeId)]: {
        ...listRefs.acm_map_level_2[pathLevelId]?.acm_map_level_3[removeDebitCreditFromKey(selectedNodeId)],
      },
    },
  }

  //remove from other maplist
  delete listRefs.acm_map_level_2[pathLevelId].acm_map_level_3[removeDebitCreditFromKey(selectedNodeId)]
}

export function checkNoteIsDebitCredit(item: StcsOrgFsdFlatItem): boolean {
  const { bsMapList } = useAcmStore()

  if (!bsMapList) {
    return false
  }

  if (!item) {
    return false
  }

  return bsMapList.acm_map_level_2[item.path_level.at(-2) ?? '']?.acm_map_level_3[item.path_level.at(-1) ?? '']?.level_3_credit_debit === AcmLevel3CreditDebitEnum.ACM_LEVEL_3_CREDIT_AND_DEBIT
}

export function checkNodeIsDebitOrCredit(node: IRowNode): AcmLevel3CreditDebitEnum {
  const { bsList } = useAcmStore()

  const hierarchy = buildHierarchy(bsList)
  const level_key_bs_total_assets = findRowByEndsWith(bsList, 'total_assets')
  const idxTotalAssets = hierarchy.findIndex((n) => n.data.row_id === level_key_bs_total_assets)

  const rootRowId = node.data.path_level[0]
  const rootIdx = hierarchy.findIndex((n) => n.data.row_id === rootRowId)

  return rootIdx < idxTotalAssets ? AcmLevel3CreditDebitEnum.ACM_LEVEL_3_DEBIT : AcmLevel3CreditDebitEnum.ACM_LEVEL_3_CREDIT
}

export function rephaseDuplicatedkey(key: string, pathLevel: string): string {
  const { bsMapList } = useAcmStore()
  if (!bsMapList) {
    return key
  }
  const l3 = bsMapList.acm_map_level_2[pathLevel]?.acm_map_level_3[key]

  if (l3?.level_3_credit_debit === AcmLevel3CreditDebitEnum.ACM_LEVEL_3_DEBIT) {
    return `debit_${key}`
  }

  if (l3?.level_3_credit_debit === AcmLevel3CreditDebitEnum.ACM_LEVEL_3_CREDIT) {
    return `credit_${key}`
  }

  return key
}

export function removeDebitCreditFromKey(key: string): string {
  return key.replace(DEBIT_CREDIT_REGEX, '')
}

export function acmValueGetter(params: ValueGetterParams | IRowNode): string {
  const { $tolgee } = useNuxtApp()

  if (!params.data) {
    return $tolgee.t('not_available')
  }

  return useAcmStore().getI18nDisplayName(params.data).name
}

export function getNonCategoriesValues_deprecated(gridApi: GridApi): string[] {
  const names = new Set<string>()
  const keys = findLevel3KeysById('level_3_non_categorized')

  gridApi.forEachLeafNode((node) => {
    if (keys.includes(node.data.row_id)) {
      const displayName = acmValueGetter(node)
      names.add(String(displayName))
    }
  })

  return Array.from(names)
}

export function getNonCategoriesValues(gridApi: GridApi): string[] {
  const names = new Set<string>()

  gridApi.forEachLeafNode((node) => {
    if (node.data.row_id.endsWith('level_2_pnl_non_categorized')) {
      const displayName = acmValueGetter(node)
      names.add(String(displayName))
    }
    if (node.data.row_id.endsWith('level_2_bs_non_categorized')) {
      const displayName = acmValueGetter(node)
      names.add(String(displayName))
    }
  })

  return Array.from(names)
}

function findLevel3KeysById(targetId: string) {
  const result: string[] = []

  const data = useAcmStore().activeFinancialStatement === StcsFstmtEnum.SFSE_PNL ? (useAcmStore().pnlMapList as StcsCompAccMappingFstmt) : (useAcmStore().bsMapList as StcsCompAccMappingFstmt)

  const lvl2 = data.acm_map_level_2 || {}

  for (const lvl2Key in lvl2) {
    const lvl3Map = lvl2[lvl2Key].acm_map_level_3
    if (!lvl3Map) {
      continue
    }

    for (const key in lvl3Map) {
      if (lvl3Map[key].level_3_id === targetId) {
        result.push(key)
      }
    }
  }

  return result
}
